/**
 * Una factura de compra: anotarla, verla y corregirla — `OD-0054` (ACCEPTED
 * 2026-10-22), plan Business para escribir.
 *
 * Una compra es una ANOTACIÓN de lo que dice la factura del proveedor (`D-01`):
 * se escriben sus bases y sus cuotas tal cual, se corrige y se anula, pero no
 * se borra. Nada de esto toca facturas emitidas ni VERI*FACTU.
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ninguna cuenta de dinero** (`URS §129`): los totales que se ven mientras
 *   se escribe los da `POST /purchases/calculate`, y la cuota propuesta, la
 *   retención propuesta y lo que no cuadra con el QR llegan como AVISOS de la
 *   API, que no bloquean: manda la factura del proveedor (`D-03`).
 * - **Ni si el QR es de la AEAT**: la lectura propone campos solo con un QR de
 *   la AEAT, y la API lo vuelve a comprobar (`D-08`).
 * - **Ni qué es el adjunto**: la API mira sus bytes (`D-07` S-03). Aquí solo se
 *   dice lo que el usuario no sabe: una foto de papel es una copia.
 *
 * El anticipo (`D-05`): al anotar la factura de ANTICIPO se ofrecen los pagos
 * sin factura del proveedor para asignarlos; al anotar la FINAL, los anticipos
 * que descuenta.
 *
 * El presupuesto (`OD-0055 D-04`): una compra puede decir de qué presupuesto
 * aceptado del mismo proveedor viene, y al elegirlo se propone su referencia
 * (`D-08`). El editor de líneas, las cifras y el adjunto los comparte la
 * pantalla del presupuesto de proveedor.
 *
 * La lectura de la factura (`OD-0061`): una foto o un PDF se leen EN EL
 * NAVEGADOR y proponen campos, solo en los huecos. El QR de la AEAT va antes
 * que el texto leído, y lo que no cuadra no se propone.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  api,
  apiCompras,
  ESTADO_VALIDACION,
  METODOS_COBRO,
  TIPOS_COMPRA,
  TIPOS_IVA_COMPRA,
  TIPOS_RETENCION_COMPRA,
  type AdjuntoCompra,
  type AltaCompra,
  type AvisoCompra,
  type CalculoCompra,
  type Compra,
  type CompraEnlazada,
  type DatosCompra,
  type DatosLineaCompra,
  type ErrorApi,
  type FichaProveedor,
  type FicheroDescargado,
  type LineaCompra,
  type MetodoCobro,
  type PagoProveedor,
  type PresupuestoEnlazado,
  type ProveedorDeLista,
  type Resultado,
  type TipoCompra,
  type TotalesCompra,
  type TratamientoIva,
} from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible, sinPuntosDeMiles } from "../ui/formato";
import { leerFactura } from "../ui/lector-factura";
import { datosDelQr } from "../ui/lector-qr";
import { propuestaDelTexto } from "../ui/lectura-factura";
import { AreaTexto, Aviso, Boton, Campo, Casilla, Selector, Tarjeta } from "../ui/primitivas";
import { diaLegible } from "./PantallaOportunidades";
import { InsigniaPago } from "./PantallaCompras";
import { TarjetaNotaAsesoria } from "./TarjetaNotaAsesoria";
import { ConversacionConLaAsesoria } from "./ConversacionConLaAsesoria";

/** El día de hoy en Madrid, AAAA-MM-DD: solo para proponer la fecha; la valida la API. */
function hoy(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(new Date());
}

export const ACEPTA_ADJUNTO = "application/pdf,image/jpeg,image/png,image/webp";
export const NUEVO = "__nuevo__";
export const SIN_RETENCION = "";
/** El valor del selector de presupuesto cuando no se enlaza ninguno. */
const SIN_PRESUPUESTO = "";

export type Mensaje = { readonly tono: "exito" | "aviso" | "error" | "info"; readonly texto: string };

/** Lo que dice la API cuando no hace lo pedido. Sus mensajes de conflicto ya están en español llano. */
export function mensajeDeRechazo(r: { estado: number; error: ErrorApi | null }): Mensaje {
  if (r.estado === 0) return { tono: "error", texto: t.compras.sinConexion };
  if (r.estado === 402) return { tono: "info", texto: t.compras.soloBusiness };
  if (r.estado === 403) return { tono: "aviso", texto: t.compras.sinPermisoEscribir };
  if ((r.estado === 409 || r.estado === 413) && r.error !== null) {
    return { tono: "aviso", texto: r.error.action === null ? r.error.message : `${r.error.message} ${r.error.action}` };
  }
  return { tono: "error", texto: t.compras.error };
}

/** Guarda el adjunto descargado con su nombre. Se descarga: no se abre en la aplicación (`D-07` S-02). */
export function guardar(contenido: Blob, nombre: string): void {
  const url = URL.createObjectURL(contenido);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function textoDeAviso(a: AvisoCompra): string {
  switch (a.code) {
    case "VAT_DIFFERS":
      return t.compras.avisos.cuota((a.line ?? 0) + 1, importeLegible(a.proposed ?? ""));
    case "WITHHOLDING_DIFFERS":
      return t.compras.avisos.retencion(importeLegible(a.proposed ?? ""));
    case "QR_TOTAL_DIFFERS":
      return t.compras.avisos.qrTotal(importeLegible(a.qrTotal ?? ""));
    case "QR_NUMBER_DIFFERS":
      return t.compras.avisos.qrNumero(a.qrNumber ?? "");
    case "QR_DATE_DIFFERS":
      return t.compras.avisos.qrFecha(diaLegible(a.qrDate ?? "1970-01-01"));
    case "QR_SUPPLIER_DIFFERS":
      return t.compras.avisos.qrProveedor(a.qrNif ?? "");
    default:
      return t.compras.avisos.otro;
  }
}

// ------------------------------------------------------------- formulario

export type LineaForm = { readonly tratamiento: TratamientoIva; readonly tipo: string; readonly base: string; readonly cuota: string };

type Form = {
  readonly proveedorId: string;
  readonly nif: string;
  readonly nombre: string;
  readonly kind: TipoCompra;
  readonly numero: string;
  readonly emision: string;
  readonly operacion: string;
  readonly vencimiento: string;
  readonly concepto: string;
  readonly referencia: string;
  readonly presupuestoId: string;
  readonly lineas: readonly LineaForm[];
  readonly retencionTipo: string;
  readonly retencion: string;
  readonly qrUrl: string | null;
  readonly anticipos: readonly string[];
  readonly pagos: readonly string[];
};

export const LINEA_VACIA: LineaForm = { tratamiento: "SUJETA_NO_EXENTA", tipo: "21,00", base: "", cuota: "" };

/** Las líneas como las da la API, para editarlas. */
export function lineasDelFormulario(lines: readonly LineaCompra[]): LineaForm[] {
  return lines.map((l) => ({ tratamiento: l.vatTreatment, tipo: l.vatRate ?? "21,00", base: l.base, cuota: l.vatTreatment === "SUJETA_NO_EXENTA" ? l.vat : "" }));
}

/** Las líneas del formulario, como las pide la API. */
export function lineasParaLaApi(lineas: readonly LineaForm[]): DatosLineaCompra[] {
  return lineas.map((l) => ({
    vatTreatment: l.tratamiento,
    vatRate: l.tratamiento === "SUJETA_NO_EXENTA" ? l.tipo : null,
    base: sinPuntosDeMiles(l.base),
    vat: l.tratamiento === "SUJETA_NO_EXENTA" ? importeONulo(l.cuota) : null,
  }));
}

/** Desde un presupuesto (`OD-0055 D-09`): el formulario ya trae proveedor, presupuesto y referencia. */
export type DesdePresupuesto = { readonly id: string; readonly referencia: string | null };

function formVacio(proveedorId: string | null, presupuesto: DesdePresupuesto | null): Form {
  return {
    proveedorId: proveedorId ?? "",
    nif: "",
    nombre: "",
    kind: "ORDINARY",
    numero: "",
    emision: "",
    operacion: "",
    vencimiento: "",
    concepto: "",
    referencia: presupuesto?.referencia ?? "",
    presupuestoId: presupuesto?.id ?? SIN_PRESUPUESTO,
    lineas: [LINEA_VACIA],
    retencionTipo: SIN_RETENCION,
    retencion: "",
    qrUrl: null,
    anticipos: [],
    pagos: [],
  };
}

function formDeCompra(c: Compra): Form {
  return {
    proveedorId: c.supplier.id,
    nif: c.supplier.taxId,
    nombre: c.supplier.name,
    kind: c.kind,
    numero: c.supplierInvoiceNumber,
    emision: c.issueDate,
    operacion: c.operationDate ?? "",
    vencimiento: c.dueDate ?? "",
    concepto: c.concept ?? "",
    referencia: c.reference ?? "",
    presupuestoId: c.quote?.id ?? SIN_PRESUPUESTO,
    lineas: lineasDelFormulario(c.lines),
    retencionTipo: c.withholdingRate ?? SIN_RETENCION,
    retencion: c.withholdingRate === null ? "" : c.withholding,
    qrUrl: c.qr?.url ?? null,
    anticipos: c.advances.map((a) => a.id),
    pagos: [],
  };
}

const nulo = (v: string): string | null => (v.trim() === "" ? null : v.trim());
/** Un importe opcional: vacío es `null`, y "1.500,50" llega como "1500,50". */
const importeONulo = (v: string): string | null => (v.trim() === "" ? null : sinPuntosDeMiles(v));

function datosDe(f: Form): DatosCompra {
  return {
    supplierInvoiceNumber: f.numero.trim(),
    issueDate: f.emision,
    operationDate: nulo(f.operacion),
    dueDate: nulo(f.vencimiento),
    concept: nulo(f.concepto),
    reference: nulo(f.referencia),
    supplierQuoteId: f.presupuestoId === SIN_PRESUPUESTO ? null : f.presupuestoId,
    lines: lineasParaLaApi(f.lineas),
    withholdingRate: f.retencionTipo === SIN_RETENCION ? null : f.retencionTipo,
    withholding: f.retencionTipo === SIN_RETENCION ? null : importeONulo(f.retencion),
    qrUrl: f.qrUrl,
    advanceIds: f.kind === "ORDINARY" ? f.anticipos : [],
  };
}

function altaDe(f: Form): AltaCompra {
  const base = { ...datosDe(f), kind: f.kind, paymentIds: f.pagos };
  return f.proveedorId === NUEVO || f.proveedorId === "" ? { ...base, supplier: { taxId: f.nif.trim(), name: f.nombre.trim() } } : { ...base, supplierId: f.proveedorId };
}

/** La foto o el PDF que se elige al anotar, con lo que la pantalla tiene que decir (`D-07`). */
export function AvisoAdjunto({ fichero }: { fichero: { readonly type: string } | null }) {
  if (fichero === null) return <p className="ayuda-neutra">{t.compras.adjunto.ayuda}</p>;
  return (
    <>
      <p className="ayuda-neutra">{fichero.type === "application/pdf" ? t.compras.adjunto.pdfOriginal : t.compras.adjunto.fotoCopia}</p>
      <p className="ayuda-neutra">{t.compras.adjunto.sinAntivirus}</p>
    </>
  );
}

/** Las líneas de IVA, como se escriben en una compra o en un presupuesto. */
export function EditorDeLineas({
  prefijo,
  ayuda,
  lineas,
  onChange,
  errorDe,
}: {
  prefijo: string;
  ayuda: string;
  lineas: readonly LineaForm[];
  onChange: (lineas: readonly LineaForm[]) => void;
  errorDe: (campo: string) => string | null;
}) {
  const cambiarLinea = (i: number, parcial: Partial<LineaForm>): void => onChange(lineas.map((l, j) => (j === i ? { ...l, ...parcial } : l)));
  return (
    <fieldset className="grupo-campos">
      <legend>{t.compras.lineas}</legend>
      <p className="ayuda-neutra">{ayuda}</p>
      {errorDe("lines") !== null ? <Aviso tono="aviso">{errorDe("lines")}</Aviso> : null}
      {lineas.map((l, i) => (
        <div key={i} className="linea-compra">
          <Selector
            id={`${prefijo}-linea-${i}-tratamiento`}
            etiqueta={t.compras.tratamiento}
            valor={l.tratamiento}
            opciones={(["SUJETA_NO_EXENTA", "SUJETA_EXENTA", "NO_SUJETA"] as const).map((v) => ({ valor: v, texto: t.compras.tratamientos[v] }))}
            onChange={(v) => cambiarLinea(i, { tratamiento: v })}
            error={errorDe(`lines.${i}.vatTreatment`)}
          />
          {l.tratamiento === "SUJETA_NO_EXENTA" ? (
            <Selector
              id={`${prefijo}-linea-${i}-tipo`}
              etiqueta={t.compras.tipoIva}
              valor={l.tipo}
              opciones={TIPOS_IVA_COMPRA.map((v) => ({ valor: v, texto: t.compras.porcentaje(v) }))}
              onChange={(v) => cambiarLinea(i, { tipo: v })}
              error={errorDe(`lines.${i}.vatRate`)}
            />
          ) : null}
          <Campo
            id={`${prefijo}-linea-${i}-base`}
            etiqueta={t.compras.base}
            valor={l.base}
            onChange={(v) => cambiarLinea(i, { base: v })}
            ayuda={t.compras.ayudaImporte}
            error={errorDe(`lines.${i}.base`)}
          />
          {l.tratamiento === "SUJETA_NO_EXENTA" ? (
            <Campo
              id={`${prefijo}-linea-${i}-cuota`}
              etiqueta={t.compras.cuota}
              requerido={false}
              valor={l.cuota}
              onChange={(v) => cambiarLinea(i, { cuota: v })}
              ayuda={t.compras.ayudaCuota}
              error={errorDe(`lines.${i}.vat`)}
            />
          ) : null}
          {lineas.length > 1 ? (
            <Boton variante="terciario" onClick={() => onChange(lineas.filter((_, j) => j !== i))}>
              {t.compras.quitarLinea}
            </Boton>
          ) : null}
        </div>
      ))}
      {lineas.length < 10 ? (
        <Boton variante="secundario" onClick={() => onChange([...lineas, LINEA_VACIA])}>
          {t.compras.anadirLinea}
        </Boton>
      ) : null}
    </fieldset>
  );
}

/** El tipo de retención y, si lo hay, el importe escrito. */
export function EditorDeRetencion({
  prefijo,
  tipo,
  importe,
  onTipo,
  onImporte,
  errorDe,
}: {
  prefijo: string;
  tipo: string;
  importe: string;
  onTipo: (v: string) => void;
  onImporte: (v: string) => void;
  errorDe: (campo: string) => string | null;
}) {
  return (
    <>
      <Selector
        id={`${prefijo}-retencion-tipo`}
        etiqueta={t.compras.retencionTipo}
        valor={tipo}
        opciones={[{ valor: SIN_RETENCION, texto: t.compras.sinRetencion }, ...TIPOS_RETENCION_COMPRA.map((v) => ({ valor: v, texto: t.compras.porcentaje(v) }))]}
        onChange={onTipo}
        error={errorDe("withholdingRate")}
      />
      {tipo !== SIN_RETENCION ? (
        <Campo
          id={`${prefijo}-retencion`}
          etiqueta={t.compras.retencion}
          requerido={false}
          valor={importe}
          onChange={onImporte}
          ayuda={t.compras.ayudaRetencion}
          error={errorDe("withholding")}
        />
      ) : null}
    </>
  );
}

/** Los totales que da la API mientras se escribe. La web no suma. */
export function CifrasCalculadas({ totales, conRetencion, etiquetaTotal }: { totales: TotalesCompra; conRetencion: boolean; etiquetaTotal: string }) {
  return (
    <dl className="cifras">
      <div>
        <dt>{t.compras.baseTotal}</dt>
        <dd>{importeLegible(totales.base)} €</dd>
      </div>
      <div>
        <dt>{t.compras.ivaTotal}</dt>
        <dd>{importeLegible(totales.vat)} €</dd>
      </div>
      <div>
        <dt>{etiquetaTotal}</dt>
        <dd>{importeLegible(totales.total)} €</dd>
      </div>
      {conRetencion ? (
        <div>
          <dt>{t.compras.retencion}</dt>
          <dd>{importeLegible(totales.withholding)} €</dd>
        </div>
      ) : null}
      <div>
        <dt>{t.compras.aPagar}</dt>
        <dd>{importeLegible(totales.payable)} €</dd>
      </div>
    </dl>
  );
}

/** El selector de presupuesto de una compra o de un pago sin factura (`OD-0055 D-04`). */
export function SelectorPresupuesto({
  id,
  etiqueta,
  ayuda,
  presupuestos,
  valor,
  onChange,
  error,
}: {
  id: string;
  etiqueta: string;
  ayuda: string;
  presupuestos: readonly PresupuestoEnlazado[];
  valor: string;
  onChange: (id: string) => void;
  error: string | null;
}) {
  if (presupuestos.length === 0 && valor === SIN_PRESUPUESTO) return null;
  return (
    <>
      <Selector
        id={id}
        etiqueta={etiqueta}
        valor={valor}
        opciones={[
          { valor: SIN_PRESUPUESTO, texto: t.compras.sinPresupuesto },
          ...presupuestos.map((q) => ({ valor: q.id, texto: t.compras.presupuestoElegible(q.quoteNumber, q.reference, importeLegible(q.toInvoice)) })),
        ]}
        onChange={onChange}
        error={error}
      />
      <p className="ayuda-neutra">{ayuda}</p>
    </>
  );
}

function ListaElegible({
  id,
  titulo,
  ayuda,
  opciones,
  elegidos,
  onChange,
}: {
  id: string;
  titulo: string;
  ayuda: string;
  opciones: ReadonlyArray<{ readonly id: string; readonly texto: string }>;
  elegidos: readonly string[];
  onChange: (ids: readonly string[]) => void;
}) {
  if (opciones.length === 0) return null;
  return (
    <fieldset className="grupo-campos">
      <legend>{titulo}</legend>
      <p className="ayuda-neutra">{ayuda}</p>
      {opciones.map((o) => (
        <Casilla
          key={o.id}
          id={`${id}-${o.id}`}
          etiqueta={o.texto}
          marcada={elegidos.includes(o.id)}
          onChange={(v) => onChange(v ? [...elegidos, o.id] : elegidos.filter((x) => x !== o.id))}
        />
      ))}
    </fieldset>
  );
}

function FormularioCompra({
  compra,
  proveedorInicial,
  presupuestoInicial,
  hayConexion,
  onGuardada,
  onCancelar,
  onSesionCaducada,
}: {
  /** `null` para anotar una nueva; la compra, para corregirla. */
  compra: Compra | null;
  proveedorInicial: string | null;
  presupuestoInicial: DesdePresupuesto | null;
  hayConexion: boolean;
  onGuardada: (c: Compra, avisos: readonly AvisoCompra[], mensaje: Mensaje | null) => void;
  onCancelar: () => void;
  onSesionCaducada: () => void;
}) {
  const alta = compra === null;
  const [form, setForm] = useState<Form>(() => (compra === null ? formVacio(proveedorInicial, presupuestoInicial) : formDeCompra(compra)));
  const [proveedores, setProveedores] = useState<readonly ProveedorDeLista[]>([]);
  const [ficha, setFicha] = useState<FichaProveedor | null>(null);
  const [calculo, setCalculo] = useState<CalculoCompra | null>(null);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [intentado, setIntentado] = useState(false);
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [fichero, setFichero] = useState<File | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const [qrTotal, setQrTotal] = useState<string | null>(null);
  // Lo que ha pasado al leer el QR se dice junto al QR, no al pie del formulario.
  const [qrMensaje, setQrMensaje] = useState<Mensaje | null>(null);
  // Un alta reintentada con mala conexión no anota dos compras (`Idempotency-Key`).
  const [clave] = useState(() => crypto.randomUUID());
  const ultimoCalculo = useRef(0);

  const cambiar = (parcial: Partial<Form>): void => setForm((f) => ({ ...f, ...parcial }));
  const formRef = useRef(form);
  formRef.current = form;

  // Los proveedores activos, para elegir uno al anotar.
  useEffect(() => {
    if (!alta) return;
    void apiCompras.listarProveedores("ACTIVE", "").then((r) => {
      if (r.ok) setProveedores(r.datos.proveedores);
      else if (r.estado === 401) onSesionCaducada();
    });
  }, [alta, onSesionCaducada]);

  // Del proveedor elegido: sus pagos sin factura y sus anticipos libres (D-05).
  useEffect(() => {
    if (!alta || form.proveedorId === "" || form.proveedorId === NUEVO) {
      setFicha(null);
      return;
    }
    let vigente = true;
    void apiCompras.obtenerProveedor(form.proveedorId).then((r) => {
      if (vigente) setFicha(r.ok ? r.datos : null);
    });
    return () => {
      vigente = false;
    };
  }, [alta, form.proveedorId]);

  // Los totales y los avisos, de la API, un momento después de dejar de escribir.
  const cuerpo = JSON.stringify(alta ? altaDe(form) : datosDe(form));
  useEffect(() => {
    const n = ++ultimoCalculo.current;
    const h = setTimeout(() => {
      void apiCompras.calcular(JSON.parse(cuerpo) as Partial<AltaCompra>).then((r) => {
        if (n !== ultimoCalculo.current) return;
        if (r.ok) setCalculo(r.datos);
        else if (r.estado === 401) onSesionCaducada();
      });
    }, 400);
    return () => clearTimeout(h);
  }, [cuerpo, onSesionCaducada]);

  // OD-0061: la factura se lee en el dispositivo y propone campos, solo en los
  // huecos. El QR de la AEAT manda sobre el texto leído; lo que no cuadra no se
  // propone (`lectura-factura.ts`), y lo que se escribe lo valida la API.
  async function leerLaFactura(f: File): Promise<void> {
    setLeyendo(true);
    setQrMensaje(null);
    const [lectura, emisor] = await Promise.all([leerFactura(f), api.obtenerEmisor()]);
    setLeyendo(false);
    if (lectura === null) return setQrMensaje({ tono: "info", texto: t.compras.qr.noLeida });
    const qr = lectura.qr === null ? null : datosDelQr(lectura.qr);
    const p = propuestaDelTexto(lectura.texto, emisor.ok ? (emisor.datos.emisor?.taxIdNormalized ?? null) : null);
    const nif = qr?.nif ?? p.nif;
    const conocido = nif === null ? undefined : proveedores.find((x) => x.taxId === nif);
    const rellenado: string[] = [];
    // El formulario de ESTE momento: mientras se leía, el usuario ha podido escribir.
    const actual = formRef.current;
    const campos = t.compras.qr.campos;
    const hueco = (v: string, propuesto: string | null, nombre: string): string => {
      if (v !== "" || propuesto === null || propuesto === "") return v;
      if (!rellenado.includes(nombre)) rellenado.push(nombre);
      return propuesto;
    };
    let proveedor: Partial<Form> = {};
    if (alta && actual.proveedorId === "" && conocido !== undefined) {
      proveedor = { proveedorId: conocido.id };
      rellenado.push(campos.proveedor);
    } else if (alta && conocido === undefined && actual.nif === "" && nif !== null && (actual.proveedorId === "" || actual.proveedorId === NUEVO)) {
      rellenado.push(campos.nif);
      proveedor = { proveedorId: NUEVO, nif, nombre: hueco(actual.nombre, p.nombre, campos.nombre) };
    }
    const lineasSinTocar = actual.lineas.every((l) => l.base.trim() === "" && l.cuota.trim() === "");
    const lineas =
      lineasSinTocar && p.lineas !== null && p.lineas.length > 0
        ? p.lineas.map((l): LineaForm => ({ tratamiento: "SUJETA_NO_EXENTA", tipo: l.tipo, base: l.base, cuota: l.cuota }))
        : null;
    if (lineas !== null) rellenado.push(campos.desglose);
    const retencion = actual.retencionTipo === SIN_RETENCION && p.retencion !== null ? p.retencion : null;
    if (retencion !== null) rellenado.push(campos.retencion);
    setForm({
      ...actual,
      ...proveedor,
      ...(qr !== null ? { qrUrl: qr.url } : {}),
      numero: hueco(actual.numero, qr?.numero ?? p.numero, campos.numero),
      emision: hueco(actual.emision, qr?.fecha ?? p.fecha, campos.fecha),
      vencimiento: hueco(actual.vencimiento, p.vencimiento, campos.vencimiento),
      ...(lineas !== null ? { lineas } : {}),
      ...(retencion !== null ? { retencionTipo: retencion.tipo, retencion: retencion.importe } : {}),
    });
    setQrTotal(qr?.total ?? null);
    // La foto o el PDF leídos suelen ser la factura: se proponen como adjunto.
    if (alta && fichero === null) setFichero(f);
    const avisos: string[] = [];
    if (lectura.qr !== null && qr === null) avisos.push(t.compras.qr.noEsAeat);
    if (rellenado.length === 0) avisos.push(t.compras.qr.nadaLeido);
    else avisos.push(t.compras.qr.rellenado(rellenado), ...(p.lineas === null ? [t.compras.qr.sinDesglose] : []), t.compras.qr.revisa);
    setQrMensaje({ tono: rellenado.length === 0 ? "info" : "exito", texto: avisos.join(" ") });
  }

  async function guardarCompra(): Promise<void> {
    setIntentado(true);
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = compra === null ? await apiCompras.anotar(altaDe(form), clave) : await apiCompras.corregir(compra.id, datosDe(form), compra.updatedAt);
    if (!r.ok) {
      setOcupado(false);
      if (r.estado === 401) return onSesionCaducada();
      if (r.estado === ESTADO_VALIDACION && r.error !== null) {
        setErrores(r.error.campos);
        return setMensaje({ tono: "aviso", texto: t.compras.revisa });
      }
      return setMensaje(mensajeDeRechazo(r));
    }
    // El adjunto va después: si no se sube, la compra ya está anotada y se dice.
    let tras: Mensaje | null = null;
    if (fichero !== null) {
      const s = await apiCompras.subirAdjunto(r.datos.compra.id, fichero);
      if (!s.ok) tras = { tono: "aviso", texto: `${t.compras.adjunto.noSubido} ${mensajeDeRechazo(s).texto}` };
    }
    setOcupado(false);
    const otra = fichero !== null && tras === null ? await apiCompras.obtener(r.datos.compra.id) : null;
    onGuardada(otra?.ok === true ? otra.datos : r.datos.compra, r.datos.avisos, tras);
  }

  const campos = intentado ? errores : {};
  const camposCalculo = calculo !== null && !calculo.ok ? calculo.campos : {};
  const errorDe = (k: string): string | null => {
    const c = campos[k];
    return c === undefined ? null : (t.compras.errores[c] ?? t.comun.errorGenerico);
  };
  const totales = calculo !== null && calculo.ok ? calculo.totales : null;
  const avisos = calculo !== null && calculo.ok ? calculo.avisos : [];
  const qrNoValido = camposCalculo.qrUrl !== undefined || campos.qrUrl !== undefined;

  const anticipos: readonly CompraEnlazada[] = alta ? (ficha?.anticiposLibres ?? []) : [...(compra?.advances ?? []), ...(compra?.availableAdvances ?? [])];
  // OD-0055 D-04: un pago de OTRO presupuesto no se puede asignar a esta factura.
  const pagos = alta ? (ficha?.sinFactura ?? []).filter((p) => p.supplierQuoteId === null || p.supplierQuoteId === form.presupuestoId) : [];
  const elegido = proveedores.find((p) => p.id === form.proveedorId);
  const presupuestos: readonly PresupuestoEnlazado[] = alta ? (ficha?.presupuestosAceptados ?? []) : (compra?.acceptedQuotes ?? []);
  const elegirPresupuesto = (id: string): void => {
    const q = presupuestos.find((x) => x.id === id);
    // D-08: al enlazarla, se propone la referencia del presupuesto.
    setForm((f) => ({
      ...f,
      presupuestoId: id,
      referencia: f.referencia.trim() === "" && q !== undefined ? (q.reference ?? "") : f.referencia,
      pagos: f.pagos.filter((pid) => (ficha?.sinFactura ?? []).some((p) => p.id === pid && (p.supplierQuoteId === null || p.supplierQuoteId === id))),
    }));
  };

  return (
    <Tarjeta titulo={compra === null ? t.compras.anotarTitulo : t.compras.corregirTitulo(compra.registryNumber)}>
      {!hayConexion ? <Aviso tono="aviso">{t.compras.sinConexion}</Aviso> : null}

      {/* D-08: el QR propone, la persona decide. */}
      <section className="grupo-campos" aria-label={t.compras.qr.titulo}>
        <h3 className="subtitulo">{t.compras.qr.titulo}</h3>
        <p className="ayuda-neutra">{t.compras.qr.ayuda}</p>
        <input
          id="compra-qr"
          className="entrada-fichero"
          type="file"
          accept="image/*,application/pdf"
          disabled={leyendo || ocupado}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f !== undefined) void leerLaFactura(f);
          }}
        />
        <label className="boton boton-secundario enlace-boton" htmlFor="compra-qr">
          {leyendo ? t.compras.qr.leyendo : t.compras.qr.leer}
        </label>
        {qrMensaje !== null ? <Aviso tono={qrMensaje.tono}>{qrMensaje.texto}</Aviso> : null}
        {form.qrUrl !== null ? (
          <div>
            {qrNoValido ? <Aviso tono="aviso">{t.compras.errores.NOT_AN_AEAT_QR}</Aviso> : <p className="ayuda-neutra">{t.compras.qr.enLaCompra}</p>}
            {qrTotal !== null && !qrNoValido ? <p className="ayuda-neutra">{t.compras.qr.total(importeLegible(qrTotal))}</p> : null}
            <div className="acciones-en-linea">
              {!qrNoValido ? (
                <a className="boton boton-secundario enlace-boton" href={form.qrUrl} target="_blank" rel="noopener noreferrer">
                  {t.compras.qr.comprobar}
                </a>
              ) : null}
              <Boton
                variante="terciario"
                onClick={() => {
                  cambiar({ qrUrl: null });
                  setQrTotal(null);
                  setQrMensaje(null);
                }}
              >
                {t.compras.qr.quitar}
              </Boton>
            </div>
          </div>
        ) : null}
      </section>

      {compra === null ? (
        <>
          <Selector
            id="compra-proveedor"
            etiqueta={t.compras.proveedor}
            valor={form.proveedorId === "" ? NUEVO : form.proveedorId}
            opciones={[{ valor: NUEVO, texto: t.compras.proveedorNuevo }, ...proveedores.map((p) => ({ valor: p.id, texto: `${p.name} · ${p.taxId}` }))]}
            onChange={(v) => cambiar({ proveedorId: v, pagos: [], anticipos: [], presupuestoId: SIN_PRESUPUESTO })}
            error={errorDe("supplierId")}
          />
          {form.proveedorId === "" || form.proveedorId === NUEVO ? (
            <>
              <Campo id="compra-nif" etiqueta={t.compras.nif} valor={form.nif} onChange={(v) => cambiar({ nif: v })} error={errorDe("taxId")} ayuda={t.compras.ayudaNif} />
              <Campo id="compra-nombre" etiqueta={t.compras.nombre} valor={form.nombre} onChange={(v) => cambiar({ nombre: v })} error={errorDe("name")} />
            </>
          ) : null}
          {elegido !== undefined && ficha !== null && ficha.sinFactura.length > 0 ? <Aviso tono="info">{t.compras.tieneSinFactura(ficha.sinFactura.length)}</Aviso> : null}
          <Selector
            id="compra-tipo"
            etiqueta={t.compras.tipo}
            valor={form.kind}
            opciones={TIPOS_COMPRA.map((k) => ({ valor: k, texto: t.compras.tipos[k] }))}
            onChange={(v) => cambiar({ kind: v })}
            error={errorDe("kind")}
          />
          <p className="ayuda-neutra">{form.kind === "ADVANCE" ? t.compras.ayudaAnticipo : t.compras.ayudaOrdinaria}</p>
        </>
      ) : (
        <p>
          {compra.supplier.name} · {compra.supplier.taxId} · {t.compras.tipos[compra.kind]}
        </p>
      )}

      <Campo id="compra-numero" etiqueta={t.compras.numero} valor={form.numero} onChange={(v) => cambiar({ numero: v })} error={errorDe("supplierInvoiceNumber")} />
      <Campo id="compra-emision" etiqueta={t.compras.emision} tipo="date" valor={form.emision} onChange={(v) => cambiar({ emision: v })} error={errorDe("issueDate")} />
      <Campo
        id="compra-operacion"
        etiqueta={t.compras.operacion}
        tipo="date"
        requerido={false}
        valor={form.operacion}
        onChange={(v) => cambiar({ operacion: v })}
        ayuda={t.compras.ayudaOperacion}
        error={errorDe("operationDate")}
      />
      <Campo
        id="compra-vencimiento"
        etiqueta={t.compras.vencimiento}
        tipo="date"
        requerido={false}
        valor={form.vencimiento}
        onChange={(v) => cambiar({ vencimiento: v })}
        error={errorDe("dueDate")}
      />
      <Campo id="compra-concepto" etiqueta={t.compras.concepto} requerido={false} valor={form.concepto} onChange={(v) => cambiar({ concepto: v })} error={errorDe("concept")} />
      <SelectorPresupuesto
        id="compra-presupuesto"
        etiqueta={t.compras.presupuesto}
        ayuda={t.compras.ayudaPresupuesto}
        presupuestos={presupuestos}
        valor={form.presupuestoId}
        onChange={elegirPresupuesto}
        error={errorDe("supplierQuoteId")}
      />
      <Campo
        id="compra-referencia"
        etiqueta={t.compras.referencia}
        requerido={false}
        valor={form.referencia}
        onChange={(v) => cambiar({ referencia: v })}
        ayuda={t.compras.ayudaReferencia}
        error={errorDe("reference")}
      />

      <EditorDeLineas prefijo="compra" ayuda={t.compras.ayudaLineas} lineas={form.lineas} onChange={(lineas) => cambiar({ lineas })} errorDe={errorDe} />
      <EditorDeRetencion
        prefijo="compra"
        tipo={form.retencionTipo}
        importe={form.retencion}
        onTipo={(v) => cambiar({ retencionTipo: v })}
        onImporte={(v) => cambiar({ retencion: v })}
        errorDe={errorDe}
      />

      {form.kind === "ORDINARY" ? (
        <ListaElegible
          id="compra-anticipo"
          titulo={t.compras.anticiposQueDescuenta}
          ayuda={t.compras.ayudaAnticipos}
          opciones={anticipos.map((a) => ({ id: a.id, texto: t.compras.enlazada(a.registryNumber, a.supplierInvoiceNumber, importeLegible(a.total)) }))}
          elegidos={form.anticipos}
          onChange={(ids) => cambiar({ anticipos: ids })}
        />
      ) : null}
      {alta ? (
        <ListaElegible
          id="compra-pago"
          titulo={t.compras.pagosQueAsignar}
          ayuda={form.kind === "ADVANCE" ? t.compras.ayudaPagosAnticipo : t.compras.ayudaPagos}
          opciones={pagos.map((p) => ({ id: p.id, texto: t.compras.pagoElegible(importeLegible(p.amount), diaLegible(p.paidOn)) }))}
          elegidos={form.pagos}
          onChange={(ids) => cambiar({ pagos: ids })}
        />
      ) : null}

      {alta ? (
        <section className="grupo-campos" aria-label={t.compras.adjunto.titulo}>
          <h3 className="subtitulo">{t.compras.adjunto.titulo}</h3>
          <input
            id="compra-adjunto"
            className="entrada-fichero"
            type="file"
            accept={ACEPTA_ADJUNTO}
            disabled={ocupado}
            onChange={(e) => setFichero(e.target.files?.[0] ?? null)}
          />
          <div className="acciones-en-linea">
            <label className="boton boton-secundario enlace-boton" htmlFor="compra-adjunto">
              {fichero === null ? t.compras.adjunto.elegir : t.compras.adjunto.cambiar}
            </label>
            {fichero !== null ? (
              <Boton variante="terciario" onClick={() => setFichero(null)}>
                {t.compras.adjunto.noAdjuntar}
              </Boton>
            ) : null}
          </div>
          {fichero !== null ? <p>{t.compras.adjunto.elegido(fichero.name)}</p> : null}
          <AvisoAdjunto fichero={fichero} />
        </section>
      ) : null}

      {/* Lo que sale de la API: totales y avisos. La web no suma. */}
      {totales !== null ? <CifrasCalculadas totales={totales} conRetencion={form.retencionTipo !== SIN_RETENCION} etiquetaTotal={t.compras.total} /> : null}
      {avisos.map((a, i) => (
        <Aviso key={`${a.code}-${i}`} tono="info">
          {textoDeAviso(a)}
        </Aviso>
      ))}

      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      <div className="acciones">
        <Boton onClick={() => void guardarCompra()} deshabilitado={ocupado || leyendo || !hayConexion}>
          {ocupado ? t.compras.guardando : alta ? t.compras.anotar : t.compras.guardarCorreccion}
        </Boton>
        <Boton variante="secundario" onClick={onCancelar}>
          {t.compras.cancelar}
        </Boton>
      </div>
    </Tarjeta>
  );
}

// ------------------------------------------------------------------ ficha

function Pagos({
  compra,
  hayConexion,
  enfocar,
  onEnfocado,
  onCompra,
  onSesionCaducada,
}: {
  compra: Compra;
  hayConexion: boolean;
  /** Se llegó desde «Apuntar pago» de la lista: el importe, a la vista y con el foco. */
  enfocar: boolean;
  onEnfocado: () => void;
  onCompra: (c: Compra) => void;
  onSesionCaducada: () => void;
}) {
  const [importe, setImporte] = useState(compra.summary.pending);
  const [fecha, setFecha] = useState(hoy);
  const [metodo, setMetodo] = useState<MetodoCobro>("TRANSFERENCIA");
  const [nota, setNota] = useState("");
  const [clave, setClave] = useState(() => crypto.randomUUID());
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [anulando, setAnulando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [asignar, setAsignar] = useState(compra.assignablePayments[0]?.id ?? "");

  const errorDe = (k: string): string | null => (errores[k] === undefined ? null : (t.compras.errores[errores[k]] ?? t.comun.errorGenerico));

  async function hecho(r: { ok: true; datos: Compra } | { ok: false; estado: number; error: ErrorApi | null }, exito: string): Promise<boolean> {
    setOcupado(false);
    if (r.ok) {
      setMensaje({ tono: "exito", texto: exito });
      setImporte(r.datos.summary.pending);
      setAsignar(r.datos.assignablePayments[0]?.id ?? "");
      onCompra(r.datos);
      return true;
    }
    if (r.estado === 401) onSesionCaducada();
    else if (r.estado === ESTADO_VALIDACION && r.error !== null) setErrores(r.error.campos);
    else setMensaje(mensajeDeRechazo(r));
    return false;
  }

  async function pagar(): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await apiCompras.pagar(compra.id, { amount: sinPuntosDeMiles(importe), paidOn: fecha, method: metodo, note: nulo(nota) }, clave);
    if (await hecho(r, t.compras.pagoAnotado)) {
      setClave(crypto.randomUUID());
      setNota("");
    }
  }

  async function anular(p: PagoProveedor): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await apiCompras.anularPago(p.id, motivo);
    if (!r.ok) {
      setOcupado(false);
      if (r.estado === 401) return onSesionCaducada();
      if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores({ motivo: r.error.campos.reason ?? "REQUIRED" });
      return setMensaje(mensajeDeRechazo(r));
    }
    setAnulando(null);
    setMotivo("");
    await hecho(await apiCompras.obtener(compra.id), t.compras.pagoAnulado);
  }

  async function asignarPago(): Promise<void> {
    setOcupado(true);
    setMensaje(null);
    await hecho(await apiCompras.asignarPago(compra.id, asignar), t.compras.pagoAsignado);
  }

  const { summary } = compra;
  const puedePagar = compra.writable && summary.blockedBy === null;
  useEffect(() => {
    if (!enfocar) return;
    const campo = puedePagar ? document.getElementById("compra-pago-importe") : null;
    campo?.scrollIntoView({ block: "center" });
    campo?.focus();
    onEnfocado();
  }, [enfocar, puedePagar, onEnfocado]);
  return (
    <section aria-label={t.compras.pagosTitulo}>
      <h3>{t.compras.pagosTitulo}</h3>
      <p>
        <InsigniaPago estado={summary.paymentStatus} />
      </p>
      <dl className="cifras">
        <div>
          <dt>{t.compras.aPagar}</dt>
          <dd>{importeLegible(summary.payable)} €</dd>
        </div>
        <div>
          <dt>{t.compras.pagado}</dt>
          <dd>{importeLegible(summary.paid)} €</dd>
        </div>
        <div>
          <dt>{t.compras.pendiente}</dt>
          <dd>{importeLegible(summary.pending)} €</dd>
        </div>
      </dl>
      {summary.blockedBy === "FULLY_PAID" ? <Aviso tono="exito">{t.compras.pagadaEntera}</Aviso> : null}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}

      {compra.payments.length > 0 ? (
        <ul className="lista-filas">
          {compra.payments.map((p) => (
            <li key={p.id} className="fila">
              <span className="fila-texto">
                <span className={p.status === "VOIDED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>{importeLegible(p.amount)} €</span>
                <span className="ayuda-neutra">
                  {diaLegible(p.paidOn)} · {t.cobros.metodos[p.method]}
                  {p.note !== null ? ` · ${p.note}` : ""}
                </span>
                {p.status === "VOIDED" ? <span className="ayuda-neutra">{t.compras.anuladoPor(p.voidReason ?? "")}</span> : null}
              </span>
              {compra.writable && p.status === "POSTED" && anulando !== p.id ? (
                <Boton variante="terciario" onClick={() => setAnulando(p.id)} deshabilitado={ocupado || !hayConexion}>
                  {t.compras.anularPago}
                </Boton>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="ayuda-neutra">{t.compras.sinPagos}</p>
      )}
      {anulando !== null ? (
        <div>
          <Campo id="compra-pago-motivo" etiqueta={t.compras.motivoAnularPago} valor={motivo} onChange={setMotivo} error={errorDe("motivo")} />
          <div className="acciones">
            <Boton
              variante="peligro"
              onClick={() => {
                const p = compra.payments.find((x) => x.id === anulando);
                if (p !== undefined) void anular(p);
              }}
              deshabilitado={ocupado || !hayConexion}
            >
              {t.compras.confirmarAnularPago}
            </Boton>
            <Boton variante="secundario" onClick={() => setAnulando(null)}>
              {t.compras.cancelar}
            </Boton>
          </div>
        </div>
      ) : null}

      {puedePagar && compra.assignablePayments.length > 0 ? (
        <div className="grupo-campos">
          <h4>{t.compras.asignarTitulo}</h4>
          <p className="ayuda-neutra">{t.compras.asignarAyuda}</p>
          <Selector
            id="compra-asignar"
            etiqueta={t.compras.pagoSinFacturaEtiqueta}
            valor={asignar}
            opciones={compra.assignablePayments.map((p) => ({ valor: p.id, texto: t.compras.pagoElegible(importeLegible(p.amount), diaLegible(p.paidOn)) }))}
            onChange={setAsignar}
          />
          <Boton variante="secundario" onClick={() => void asignarPago()} deshabilitado={ocupado || !hayConexion || asignar === ""}>
            {t.compras.asignar}
          </Boton>
        </div>
      ) : null}

      {puedePagar ? (
        <div className="grupo-campos">
          <h4>{t.compras.nuevoPago}</h4>
          <Campo id="compra-pago-importe" etiqueta={t.compras.importe} valor={importe} onChange={setImporte} ayuda={t.compras.ayudaImporte} error={errorDe("amount")} />
          <Campo id="compra-pago-fecha" etiqueta={t.compras.fechaPago} tipo="date" valor={fecha} onChange={setFecha} error={errorDe("paidOn")} />
          <Selector
            id="compra-pago-metodo"
            etiqueta={t.compras.metodo}
            valor={metodo}
            opciones={METODOS_COBRO.map((m) => ({ valor: m, texto: t.cobros.metodos[m] ?? m }))}
            onChange={setMetodo}
            error={errorDe("method")}
          />
          <AreaTexto id="compra-pago-nota" etiqueta={t.compras.nota} valor={nota} onChange={setNota} requerido={false} error={errorDe("note")} />
          <Boton onClick={() => void pagar()} deshabilitado={ocupado || !hayConexion}>
            {ocupado ? t.compras.guardando : t.compras.anotarPago}
          </Boton>
        </div>
      ) : null}
    </section>
  );
}

function Adjunto({ compra, hayConexion, onCompra, onSesionCaducada }: { compra: Compra; hayConexion: boolean; onCompra: (c: Compra) => void; onSesionCaducada: () => void }) {
  return (
    <AdjuntoDocumento
      prefijo="compra-ficha"
      titulo={t.compras.adjunto.titulo}
      adjunto={compra.attachment}
      puede={compra.writable}
      hayConexion={hayConexion}
      deUnaFactura
      textoQuitado={t.compras.adjunto.quitado}
      subir={(f) => apiCompras.subirAdjunto(compra.id, f)}
      quitar={() => apiCompras.quitarAdjunto(compra.id)}
      descargar={() => apiCompras.descargarAdjunto(compra.id)}
      recargar={async () => {
        const otra = await apiCompras.obtener(compra.id);
        if (otra.ok) onCompra(otra.datos);
      }}
      onSesionCaducada={onSesionCaducada}
    />
  );
}

/**
 * Las llamadas del adjunto. Firmas de llamada y no flechas porque el guardrail
 * de textos lee línea a línea, y `=> Promise<` le parece texto de JSX.
 */
interface SubidaDeAdjunto {
  (f: File): Promise<Resultado<true>>;
}
interface PeticionDeAdjunto<T> {
  (): Promise<Resultado<T>>;
}
interface Recarga {
  (): Promise<void>;
}

/**
 * El adjunto de un documento anotado: el de una compra y el de un presupuesto
 * de proveedor. Solo el de una factura dice si es el original o una copia
 * (`OD-0054 D-07`): un presupuesto no es un documento fiscal.
 */
export function AdjuntoDocumento({
  prefijo,
  titulo,
  adjunto: a,
  puede,
  hayConexion,
  deUnaFactura = false,
  textoQuitado,
  subir: subirFichero,
  quitar: quitarFichero,
  descargar: descargarFichero,
  recargar,
  onSesionCaducada,
}: {
  prefijo: string;
  titulo: string;
  adjunto: AdjuntoCompra | null;
  puede: boolean;
  hayConexion: boolean;
  deUnaFactura?: boolean;
  textoQuitado: string;
  subir: SubidaDeAdjunto;
  quitar: PeticionDeAdjunto<true>;
  descargar: PeticionDeAdjunto<FicheroDescargado>;
  recargar: Recarga;
  onSesionCaducada: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [elegido, setElegido] = useState<{ readonly type: string } | null>(null);

  async function tras(r: { ok: true } | { ok: false; estado: number; error: ErrorApi | null }, exito: string): Promise<void> {
    if (!r.ok) {
      setOcupado(false);
      if (r.estado === 401) return onSesionCaducada();
      return setMensaje(mensajeDeRechazo(r));
    }
    await recargar();
    setOcupado(false);
    setMensaje({ tono: "exito", texto: exito });
  }

  async function subir(f: File): Promise<void> {
    setOcupado(true);
    setMensaje(null);
    setElegido(f);
    await tras(await subirFichero(f), t.compras.adjunto.subido);
  }

  async function descargar(): Promise<void> {
    setOcupado(true);
    setMensaje(null);
    const r = await descargarFichero();
    setOcupado(false);
    if (r.ok) return guardar(r.datos.contenido, r.datos.nombre);
    if (r.estado === 401) return onSesionCaducada();
    setMensaje(mensajeDeRechazo(r));
  }

  const idEntrada = `${prefijo}-adjunto`;
  return (
    <section aria-label={titulo}>
      <h3>{titulo}</h3>
      {a !== null ? (
        <p>
          {t.compras.adjunto.resumen(a.originalName, a.tamanoLegible)}
          {deUnaFactura ? (
            <>
              <br />
              <span className="ayuda-neutra">{a.contentType === "application/pdf" ? t.compras.adjunto.pdfOriginal : t.compras.adjunto.fotoCopia}</span>
            </>
          ) : null}
        </p>
      ) : (
        <p className="ayuda-neutra">{t.compras.adjunto.ninguno}</p>
      )}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      <div className="acciones-en-linea">
        {a !== null ? (
          <Boton variante="secundario" onClick={() => void descargar()} deshabilitado={ocupado || !hayConexion}>
            {t.compras.adjunto.descargar}
          </Boton>
        ) : null}
        {puede ? (
          <>
            <input
              id={idEntrada}
              className="entrada-fichero"
              type="file"
              accept={ACEPTA_ADJUNTO}
              disabled={ocupado || !hayConexion}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f !== undefined) void subir(f);
              }}
            />
            <label className="boton boton-secundario enlace-boton" htmlFor={idEntrada}>
              {ocupado ? t.compras.adjunto.subiendo : a === null ? t.compras.adjunto.elegir : t.compras.adjunto.sustituir}
            </label>
          </>
        ) : null}
        {puede && a !== null ? (
          <Boton
            variante="terciario"
            onClick={() => {
              setOcupado(true);
              setMensaje(null);
              void quitarFichero().then((r) => tras(r, textoQuitado));
            }}
            deshabilitado={ocupado || !hayConexion}
          >
            {t.compras.adjunto.quitar}
          </Boton>
        ) : null}
      </div>
      {puede && deUnaFactura ? <AvisoAdjunto fichero={elegido} /> : null}
    </section>
  );
}

function FichaCompra({
  compra,
  hayConexion,
  mensajeInicial,
  enfocarPago,
  onPagoEnfocado,
  onCompra,
  onCorregir,
  onAbrir,
  onAbrirProveedor,
  onAbrirPresupuesto,
  onSesionCaducada,
}: {
  compra: Compra;
  hayConexion: boolean;
  mensajeInicial: Mensaje | null;
  enfocarPago: boolean;
  onPagoEnfocado: () => void;
  onCompra: (c: Compra) => void;
  onCorregir: () => void;
  onAbrir: (id: string) => void;
  onAbrirProveedor: (id: string) => void;
  onAbrirPresupuesto: (id: string) => void;
  onSesionCaducada: () => void;
}) {
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<Mensaje | null>(mensajeInicial);
  const [ocupado, setOcupado] = useState(false);

  async function anular(): Promise<void> {
    setOcupado(true);
    setError(null);
    setMensaje(null);
    const r = await apiCompras.anular(compra.id, motivo);
    setOcupado(false);
    if (r.ok) {
      setAnulando(false);
      setMensaje({ tono: "exito", texto: t.compras.anuladaOk });
      return onCompra(r.datos);
    }
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === ESTADO_VALIDACION && r.error !== null) return setError(t.compras.errores[r.error.campos.reason ?? "REQUIRED"] ?? t.comun.errorGenerico);
    setMensaje(mensajeDeRechazo(r));
  }

  const c = compra;
  return (
    <Tarjeta titulo={t.compras.fichaTitulo(c.registryNumber)}>
      <p className="fila-titulo">
        <button type="button" className="enlace-en-texto" onClick={() => onAbrirProveedor(c.supplier.id)}>
          {c.supplier.name}
        </button>
        {` · ${c.supplier.taxId}`}
      </p>
      <p>
        <span className="insignia">{t.compras.tipos[c.kind]}</span>
        {c.status === "VOIDED" ? (
          <>
            {" "}
            <span className="insignia">{t.compras.anulada}</span>
          </>
        ) : null}
      </p>
      {c.status === "VOIDED" ? <Aviso tono="info">{t.compras.anuladaPor(c.voidReason ?? "")}</Aviso> : null}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}

      <dl className="datos">
        <div>
          <dt>{t.compras.numero}</dt>
          <dd>{c.supplierInvoiceNumber}</dd>
        </div>
        <div>
          <dt>{t.compras.emision}</dt>
          <dd>{diaLegible(c.issueDate)}</dd>
        </div>
        {c.operationDate !== null ? (
          <div>
            <dt>{t.compras.datos.operacion}</dt>
            <dd>{diaLegible(c.operationDate)}</dd>
          </div>
        ) : null}
        <div>
          <dt>{t.compras.registrada}</dt>
          <dd>{diaLegible(c.receivedOn)}</dd>
        </div>
        {c.dueDate !== null ? (
          <div>
            <dt>{t.compras.datos.vencimiento}</dt>
            <dd>{diaLegible(c.dueDate)}</dd>
          </div>
        ) : null}
        {c.concept !== null ? (
          <div>
            <dt>{t.compras.datos.concepto}</dt>
            <dd>{c.concept}</dd>
          </div>
        ) : null}
        {c.quote !== null ? (
          <div>
            <dt>{t.compras.datos.presupuesto}</dt>
            <dd>
              <button type="button" className="enlace-en-texto" onClick={() => onAbrirPresupuesto(c.quote?.id ?? "")}>
                {c.quote.quoteNumber}
              </button>
            </dd>
          </div>
        ) : null}
        {c.reference !== null ? (
          <div>
            <dt>{t.compras.datos.referencia}</dt>
            <dd>{c.reference}</dd>
          </div>
        ) : null}
      </dl>

      <h3>{t.compras.lineas}</h3>
      <ul className="lista-filas">
        {c.lines.map((l, i) => (
          <li key={i} className="fila">
            <span className="fila-texto">
              <span className="fila-titulo">{l.vatRate !== null ? t.compras.porcentaje(l.vatRate) : t.compras.tratamientos[l.vatTreatment]}</span>
              <span className="ayuda-neutra">{t.compras.lineaResumen(importeLegible(l.base), importeLegible(l.vat))}</span>
            </span>
          </li>
        ))}
      </ul>
      <dl className="cifras">
        <div>
          <dt>{t.compras.baseTotal}</dt>
          <dd>{importeLegible(c.base)} €</dd>
        </div>
        <div>
          <dt>{t.compras.ivaTotal}</dt>
          <dd>{importeLegible(c.vat)} €</dd>
        </div>
        <div>
          <dt>{t.compras.total}</dt>
          <dd>{importeLegible(c.total)} €</dd>
        </div>
        {c.withholdingRate !== null ? (
          <div>
            <dt>{t.compras.retencionDe(t.compras.porcentaje(c.withholdingRate))}</dt>
            <dd>{importeLegible(c.withholding)} €</dd>
          </div>
        ) : null}
      </dl>

      {/* D-05: la final enseña la operación completa; si el proveedor ha vuelto a cobrar el IVA del anticipo, se ve aquí. */}
      {c.advances.length > 0 ? (
        <section aria-label={t.compras.anticiposDescontados}>
          <h3>{t.compras.anticiposDescontados}</h3>
          <ul className="lista-filas">
            {c.advances.map((a) => (
              <li key={a.id} className="fila">
                <button type="button" className="enlace-en-texto" onClick={() => onAbrir(a.id)}>
                  {t.compras.enlazada(a.registryNumber, a.supplierInvoiceNumber, importeLegible(a.total))}
                </button>
              </li>
            ))}
          </ul>
          {c.operationTotal !== null ? <p>{t.compras.operacionCompleta(importeLegible(c.operationTotal))}</p> : null}
          <p className="ayuda-neutra">{t.compras.ayudaOperacionCompleta}</p>
        </section>
      ) : null}
      {c.discountedBy !== null ? (
        <p>
          {t.compras.descontadoEn}{" "}
          <button type="button" className="enlace-en-texto" onClick={() => onAbrir(c.discountedBy?.id ?? "")}>
            {t.compras.enlazada(c.discountedBy.registryNumber, c.discountedBy.supplierInvoiceNumber, importeLegible(c.discountedBy.total))}
          </button>
        </p>
      ) : null}

      {c.qr !== null ? (
        <section aria-label={t.compras.qr.titulo}>
          <h3>{t.compras.qr.titulo}</h3>
          <p className="ayuda-neutra">{c.qr.verifactu ? t.compras.qr.verifactu : t.compras.qr.noVerifactu}</p>
          <a className="boton boton-secundario enlace-boton" href={c.qr.url} target="_blank" rel="noopener noreferrer">
            {t.compras.qr.comprobar}
          </a>
        </section>
      ) : null}

      <Adjunto compra={c} hayConexion={hayConexion} onCompra={onCompra} onSesionCaducada={onSesionCaducada} />
      <Pagos
        key={c.updatedAt + c.summary.paid}
        compra={c}
        hayConexion={hayConexion}
        enfocar={enfocarPago}
        onEnfocado={onPagoEnfocado}
        onCompra={onCompra}
        onSesionCaducada={onSesionCaducada}
      />

      {c.writable ? (
        <div className="acciones">
          <Boton variante="secundario" onClick={onCorregir} deshabilitado={!hayConexion}>
            {t.compras.corregir}
          </Boton>
          {!anulando ? (
            <Boton variante="peligro" onClick={() => setAnulando(true)} deshabilitado={!hayConexion}>
              {t.compras.anular}
            </Boton>
          ) : null}
        </div>
      ) : null}
      {anulando ? (
        <div>
          <p className="ayuda-neutra">{t.compras.ayudaAnular}</p>
          <Campo id="compra-motivo" etiqueta={t.compras.motivoAnular} valor={motivo} onChange={setMotivo} error={error} />
          <div className="acciones">
            <Boton variante="peligro" onClick={() => void anular()} deshabilitado={ocupado || !hayConexion}>
              {t.compras.confirmarAnular}
            </Boton>
            <Boton variante="secundario" onClick={() => setAnulando(false)}>
              {t.compras.cancelar}
            </Boton>
          </div>
        </div>
      ) : null}
    </Tarjeta>
  );
}

// --------------------------------------------------------------- pantalla

type Vista =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "no-encontrada" }
  | { readonly tipo: "ficha"; readonly compra: Compra; readonly mensaje: Mensaje | null }
  | { readonly tipo: "corregir"; readonly compra: Compra };

export function PantallaCompra({
  id,
  proveedorInicial,
  presupuestoInicial = null,
  alPago = false,
  hayConexion,
  onVolver,
  onAbrirProveedor,
  onAbrirPresupuesto,
  onSesionCaducada,
}: {
  id: string | null;
  proveedorInicial: string | null;
  /** Anotar la factura DE un presupuesto (`OD-0055 D-04`): llega elegido y con su referencia. */
  presupuestoInicial?: DesdePresupuesto | null;
  /** Abrir con el formulario de pago a la vista (la acción «Apuntar pago» de la lista). */
  alPago?: boolean;
  hayConexion: boolean;
  onVolver: () => void;
  onAbrirProveedor: (id: string) => void;
  onAbrirPresupuesto: (id: string) => void;
  onSesionCaducada: () => void;
}) {
  // Tras anotar, la misma pantalla enseña la ficha de lo anotado, con su aviso.
  const [actual, setActual] = useState<string | null>(id);
  const [vista, setVista] = useState<Vista>({ tipo: "cargando" });
  // La compra recién anotada ya está en `vista`: no se vuelve a pedir, que borraría su aviso.
  const yaCargada = useRef<string | null>(null);
  const [enfocarPago, setEnfocarPago] = useState(alPago);
  const pagoEnfocado = useCallback(() => setEnfocarPago(false), []);

  const cargar = useCallback(
    async (compraId: string, mensaje: Mensaje | null = null): Promise<void> => {
      setVista({ tipo: "cargando" });
      const r = await apiCompras.obtener(compraId);
      if (r.ok) return setVista({ tipo: "ficha", compra: r.datos, mensaje });
      if (r.estado === 401) return onSesionCaducada();
      setVista({ tipo: r.estado === 404 ? "no-encontrada" : "error" });
    },
    [onSesionCaducada],
  );

  useEffect(() => {
    if (actual === null || yaCargada.current === actual) return;
    void cargar(actual);
  }, [actual, cargar]);

  const guardada = (c: Compra, avisos: readonly AvisoCompra[], tras: Mensaje | null): void => {
    const texto = [t.compras.guardada(c.registryNumber), ...avisos.map(textoDeAviso)].join(" ");
    setVista({ tipo: "ficha", compra: c, mensaje: tras ?? { tono: avisos.length > 0 ? "info" : "exito", texto } });
    yaCargada.current = c.id;
    setActual(c.id);
  };

  const volver = (
    <div className="barra-acciones">
      <Boton variante="secundario" onClick={onVolver}>
        {t.compras.volver}
      </Boton>
    </div>
  );

  if (actual === null) {
    return (
      <>
        {volver}
        <FormularioCompra
          compra={null}
          proveedorInicial={proveedorInicial}
          presupuestoInicial={presupuestoInicial}
          hayConexion={hayConexion}
          onGuardada={guardada}
          onCancelar={onVolver}
          onSesionCaducada={onSesionCaducada}
        />
      </>
    );
  }

  return (
    <>
      {volver}
      {vista.tipo === "cargando" ? <p>{t.compras.cargando}</p> : null}
      {vista.tipo === "no-encontrada" ? <Aviso tono="aviso">{t.compras.noEncontrada}</Aviso> : null}
      {vista.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.compras.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar(actual)}>
            {t.compras.reintentar}
          </Boton>
        </>
      ) : null}
      {vista.tipo === "ficha" ? (
        <FichaCompra
          key={vista.compra.id}
          compra={vista.compra}
          hayConexion={hayConexion}
          mensajeInicial={vista.mensaje}
          enfocarPago={enfocarPago}
          onPagoEnfocado={pagoEnfocado}
          onCompra={(c) => setVista({ tipo: "ficha", compra: c, mensaje: null })}
          onCorregir={() => setVista({ tipo: "corregir", compra: vista.compra })}
          onAbrir={(otra) => {
            yaCargada.current = null;
            setActual(otra);
          }}
          onAbrirProveedor={onAbrirProveedor}
          onAbrirPresupuesto={onAbrirPresupuesto}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}
      {/* OD-0067 D-02: lo que el cliente le dice a su gestoria de ESTA compra.
          Solo de las anotadas: una anulada no sale en la exportacion. */}
      {vista.tipo === "ficha" && vista.compra.status === "REGISTERED" ? (
        <TarjetaNotaAsesoria key={vista.compra.id} documento="compra" id={vista.compra.id} hayConexion={hayConexion} onSesionCaducada={onSesionCaducada} />
      ) : null}
      {/* OD-0067 D-05: lo que se habla con la gestoria de ESTA compra. */}
      {vista.tipo === "ficha" && vista.compra.status === "REGISTERED" ? (
        <ConversacionConLaAsesoria
          key={`conversacion-${vista.compra.id}`}
          organizationId={null}
          documento={{ tipo: "compra", id: vista.compra.id, etiqueta: vista.compra.supplierInvoiceNumber }}
          hayConexion={hayConexion}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}
      {vista.tipo === "corregir" ? (
        <FormularioCompra
          compra={vista.compra}
          proveedorInicial={null}
          presupuestoInicial={null}
          hayConexion={hayConexion}
          onGuardada={guardada}
          onCancelar={() => setVista({ tipo: "ficha", compra: vista.compra, mensaje: null })}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}
    </>
  );
}
